import { useState } from "react";
import { Link, NavLink, useLocation, useParams } from "react-router-dom";
import {
  Bookmark,
  CalendarDays,
  Compass,
  Home,
  Map,
  Search,
} from "lucide-react";
import { LOCALES, useI18n, type Locale } from "../i18n";
import { useAuth } from "../auth";
import { GlobalSearchModal } from "../components/HeroSearch";
import { cn } from "@/lib/utils";

const LOCALE_LABELS: Record<Locale, string> = {
  en: "EN",
  ar: "عر",
  ku: "کورد",
};

export function AppShell({ children }: { children: React.ReactNode }) {
  const { locale, t, setLocale } = useI18n();
  const { user, logout } = useAuth();
  const params = useParams();
  const location = useLocation();
  const currentLocale = (params.locale as Locale) || locale;
  const [searchOpen, setSearchOpen] = useState(false);

  const base = `/${currentLocale}`;
  const restPath = location.pathname.replace(/^\/(en|ar|ku)/, "") || "";
  const restForMatch = restPath || "/";

  const nav = [
    { to: `${base}/explore`, label: t("nav.explore") },
    { to: `${base}/destinations`, label: t("nav.destinations") },
    { to: `${base}/experiences`, label: t("nav.experiences") },
    { to: `${base}/plan-trip`, label: t("nav.planTrip") },
    { to: `${base}/map`, label: t("nav.map") },
    { to: `${base}/travel-guide`, label: t("nav.travelGuide") },
    { to: `${base}/saved`, label: t("nav.saved") },
  ];

  const mobile = [
    { to: base, label: t("mobile.home"), icon: Home, match: restForMatch === "/" },
    {
      to: `${base}/destinations`,
      label: t("mobile.explore"),
      icon: Compass,
      match: restForMatch.startsWith("/destinations") || restForMatch.startsWith("/explore"),
    },
    {
      to: `${base}/plan-trip`,
      label: t("mobile.plan"),
      icon: CalendarDays,
      match: restForMatch.startsWith("/plan-trip"),
    },
    {
      to: `${base}/map`,
      label: t("mobile.map"),
      icon: Map,
      match: restForMatch.startsWith("/map"),
    },
    {
      to: `${base}/saved`,
      label: t("mobile.saved"),
      icon: Bookmark,
      match: restForMatch.startsWith("/saved"),
    },
  ];

  return (
    <div className="min-h-screen bg-sand text-ink">
      <header className="sticky top-0 z-40 border-b border-mist/80 bg-sand/95 backdrop-blur-md">
        <div className="mx-auto flex h-14 max-w-7xl items-center justify-between gap-4 px-4 sm:h-16 sm:px-6 lg:px-8">
          <Link to={base} className="font-display text-lg font-bold tracking-tight text-ink sm:text-xl">
            {t("meta.siteName")}
          </Link>
          <nav className="hidden items-center gap-5 lg:flex" aria-label="Main">
            {nav.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  cn(
                    "text-sm font-medium transition-colors hover:text-pine",
                    isActive ? "text-pine" : "text-ridge",
                  )
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-2 sm:gap-3">
            <button
              type="button"
              onClick={() => setSearchOpen(true)}
              className="rounded-full p-2 text-ridge hover:bg-mist hover:text-pine"
              aria-label={t("nav.search")}
            >
              <Search className="size-5" />
            </button>
            <div className="flex items-center gap-1 text-xs" role="group" aria-label="Language">
              {LOCALES.map((loc, i) => (
                <span key={loc} className="inline-flex items-center">
                  {i > 0 ? <span className="mx-1 text-ridge/30">|</span> : null}
                  <Link
                    to={`/${loc}${restPath}`}
                    onClick={() => setLocale(loc)}
                    className={cn(
                      "hover:text-pine",
                      loc === currentLocale ? "font-semibold text-pine" : "text-ridge/70",
                    )}
                  >
                    {LOCALE_LABELS[loc]}
                  </Link>
                </span>
              ))}
            </div>
            {user ? (
              <>
                <Link
                  to={`${base}/profile`}
                  className="hidden text-sm font-medium text-ridge hover:text-pine sm:inline"
                >
                  {t("nav.profile")}
                </Link>
                {["editor", "admin", "super_admin"].includes(user.role) ? (
                  <Link to={`${base}/admin`} className="hidden text-sm font-medium text-ridge hover:text-pine md:inline">
                    {t("nav.admin")}
                  </Link>
                ) : null}
                <button
                  type="button"
                  onClick={() => void logout()}
                  className="text-sm font-medium text-ridge hover:text-pine"
                >
                  {t("auth.logout")}
                </button>
              </>
            ) : (
              <Link to={`${base}/login`} className="text-sm font-medium text-ridge hover:text-pine">
                {t("nav.login")}
              </Link>
            )}
          </div>
        </div>
      </header>

      <main className="pb-20 md:pb-0">{children}</main>

      <footer className="mt-16 border-t border-mist bg-ink py-10 text-sand">
        <div className="mx-auto max-w-7xl px-4 text-sm text-mist/80 sm:px-6 lg:px-8">
          <p className="font-display text-lg font-semibold text-sand">{t("meta.siteName")}</p>
          <p className="mt-2 max-w-xl">{t("home.trustLine")}</p>
        </div>
      </footer>

      <nav
        className="fixed inset-x-0 bottom-0 z-50 border-t border-mist bg-sand/95 backdrop-blur-md md:hidden"
        aria-label="Mobile navigation"
      >
        <ul className="mx-auto flex max-w-lg items-stretch justify-around px-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] pt-1">
          {mobile.map((item) => {
            const Icon = item.icon;
            return (
              <li key={item.to} className="flex-1">
                <Link
                  to={item.to}
                  className={cn(
                    "flex flex-col items-center gap-0.5 px-2 py-2 text-[10px] font-medium transition-colors",
                    item.match ? "text-pine" : "text-ridge/60 hover:text-ridge",
                  )}
                  aria-current={item.match ? "page" : undefined}
                >
                  <Icon className={cn("size-5", item.match && "stroke-[2.5]")} aria-hidden />
                  <span>{item.label}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      <GlobalSearchModal open={searchOpen} onClose={() => setSearchOpen(false)} />
    </div>
  );
}
